import { MenuIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';

import { ErrorBoundary } from '@/components/common/error-boundary';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

import { Sidebar } from './sidebar';
import { Topbar } from './topbar';

/**
 * The signed-in shell: persistent sidebar on desktop, a sheet on mobile.
 *
 * The sidebar is rendered once and kept mounted across navigations so that
 * scroll position and any future expanded sections survive route changes.
 * `ErrorBoundary` wraps the page outlet so a crashed feature cannot blank the chrome.
 */
export function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Navigating from inside the mobile sheet must close it; otherwise the new
  // page renders behind an overlay the editor has to dismiss by hand.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-svh overflow-hidden bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:ring-2 focus:ring-ring"
      >
        Skip to content
      </a>

      {/* Fixed chrome: sidebar never scrolls with the page. Nav can overflow
          internally if the item list exceeds the viewport. */}
      <aside className="hidden h-full w-64 shrink-0 overflow-hidden lg:block">
        <Sidebar />
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu">
                <MenuIcon />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              showCloseButton={false}
              className="w-64 gap-0 border-sidebar-border bg-sidebar p-0"
            >
              <SheetTitle className="sr-only">Main navigation</SheetTitle>
              <Sidebar />
            </SheetContent>
          </Sheet>
        </Topbar>

        <main
          id="main"
          className="min-h-0 min-w-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8"
        >
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
